// src/affinity_core_planning.jsx
// ─────────────────────────────────────────────────────────────────────────────
// AFFINITY CORE — PLANNING (budgeting, forecasting, scenarios)
//
// Builds only what Core did not already have. The calculation engine already
// exists on the accounting-engine branch (budget, budget_line, submit_budget,
// approve_budget, build_rolling_forecast, compare_budget_scenarios), so this is
// the interface layer over it:
//
//   1. Budget input  — spreadsheet-style grid: accounts down, periods across,
//      frozen header and first column, keyboard navigation, multi-cell paste,
//      spreading, autosave, undo, and visually distinct cell types.
//   2. Workflow      — Not started / In progress / Submitted / Returned /
//      Approved / Locked, with owner, approver, due date and a validation gate.
//   3. Scenarios     — create from an approved budget, compare, lock, promote.
//
// Design follows Core, not the source spec's tokens: Midnight Navy, Affinity
// Cyan, Catamaran, hairline borders, no rounded card per row, tabular figures
// right-aligned.
//
// Rows resolve against preview data until accounting-engine is merged and
// db/001-051 is run; every write below maps to an RPC that already exists.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { budgetList, budgetGrid, setBudgetCell, submitBudget, approveBudget, pivotToGrid, compareScenarios, budgetSummary } from "./affinity_planning_api";
import { planningScenarioCreate } from "./affinity_docs_onb_write_api";
import { phaseFee, phaseFees, phaseHeadcount, phaseStaffCost, projectBalanceSheet, daysInMonth,
         FREQUENCIES, PHASING, COST_CENTRES, BUDGET_STAGES,
         ONCOSTS_BY_REGION, ENTITY_REGION,
         computeRecharges, rechargeSummary, convert, BUDGET_FX,
         MAX_RECHARGE_TARGETS, GROUP_ALLOCATION, GROUP_REF } from "./affinity_budget_model";

const NAVY = "#001242", CY = "#00C4CC";
const INK  = "var(--text-primary,#111)";
const MUT  = "var(--text-secondary,#666)";
const LINE = "var(--border-tertiary,#e5e5e5)";
const CARD = "var(--bg-primary,#fff)";
const PAGE = "var(--bg-secondary,#f8f9fc)";
const SUBTLE = "var(--bg-secondary,#f9f9f9)";
const POS = "#4CAF7D", NEG = "#EF4444", AMBER = "#F59E0B";
const FONT = "'Catamaran',system-ui,sans-serif";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// ── Chart of accounts for planning. kind drives cell behaviour:
//    input = editable, calc = derived subtotal, actual = posted from the ledger
const ACCOUNTS = [
  { group:"Revenue",       code:"4000", name:"Company administration fees", kind:"linked", src:"fees" },
  { group:"Revenue",       code:"4010", name:"Trustee fees",                kind:"linked", src:"fees" },
  { group:"Revenue",       code:"4020", name:"Directorship fees",           kind:"linked", src:"fees" },
  { group:"Revenue",       code:"4030", name:"Registered office fees",      kind:"linked", src:"fees" },
  { group:"Revenue",       code:"4040", name:"Time-based / ad hoc",         kind:"linked", src:"fees" },
  { group:"Revenue",       code:"4090", name:"Disbursements recovered",     kind:"linked", src:"fees" },
  { group:"Direct costs",  code:"5000", name:"Government & registry fees",  kind:"input" },
  { group:"Direct costs",  code:"5010", name:"Sub-contracted services",     kind:"input" },
  { group:"Staff costs",   code:"6000", name:"Salaries",                    kind:"linked", src:"staff", el:"salary" },
  { group:"Staff costs",   code:"6010", name:"Employer social",             kind:"linked", src:"staff", el:"social" },
  { group:"Staff costs",   code:"6020", name:"Pension contributions",       kind:"linked", src:"staff", el:"pension" },
  { group:"Staff costs",   code:"6025", name:"Bonuses",                     kind:"linked", src:"staff", el:"bonus" },
  { group:"Staff costs",   code:"6026", name:"Healthcare, wellness, cinema",kind:"linked", src:"staff", el:"benefits" },
  { group:"Staff costs",   code:"6030", name:"Recruitment & training",      kind:"input" },
  { group:"Staff recharges", code:"6040", name:"Recharged in — direct",       kind:"linked", src:"staff" },
  { group:"Staff recharges", code:"6041", name:"Recharged in — via group",    kind:"linked", src:"staff" },
  { group:"Staff recharges", code:"6045", name:"Recharged out — direct",      kind:"linked", src:"staff", credit:true },
  { group:"Staff recharges", code:"6046", name:"Recharged out — on to subsidiaries", kind:"linked", src:"staff", credit:true },
  { group:"Overheads",     code:"7000", name:"Premises & rates",            kind:"input" },
  { group:"Overheads",     code:"7010", name:"IT & software",               kind:"input" },
  { group:"Overheads",     code:"7020", name:"Professional indemnity",      kind:"input" },
  { group:"Overheads",     code:"7030", name:"Regulatory & licence fees",   kind:"input" },
  { group:"Overheads",     code:"7040", name:"Travel & entertaining",       kind:"input" },
  { group:"Overheads",     code:"7050", name:"Depreciation",                kind:"actual" },
];

const GROUPS = ["Revenue","Direct costs","Staff costs","Staff recharges","Overheads"];
const SIGN = { "Revenue":1, "Direct costs":-1, "Staff costs":-1, "Staff recharges":-1, "Overheads":-1 };

const ENTITIES = [
  { ref:"AFG-000", name:"Affinity Group Limited",        ccy:"GBP" },
  { ref:"AFG-IOM", name:"Affinity (Isle of Man) Limited",ccy:"GBP" },
  { ref:"AFG-MLT", name:"Affinity (Malta) Limited",      ccy:"EUR" },
  { ref:"AFG-CYM", name:"Affinity (Cayman) Limited",     ccy:"USD" },
  { ref:"AFG-UK",  name:"Affinity (UK) Limited",         ccy:"GBP" },
  { ref:"AFG-CYP", name:"Affinity (Cyprus) Limited",     ccy:"EUR" },
  { ref:"AFG-SD",  name:"Affinity South Dakota, LLC",    ccy:"USD" },
  { ref:"AFG-FL",  name:"Affinity South Florida, LLC",   ccy:"USD" },
];

const STATES = ["Not started","In progress","Submitted","Returned","Approved","Locked"];
const STATE_STYLE = {
  "Not started": { bg:"#F2F2F2", color:"#777" },
  "In progress": { bg:"#EAF0FB", color:"#274690" },
  "Submitted":   { bg:"#FDF4DC", color:"#7B4F1D" },
  "Returned":    { bg:"#FCEBEB", color:"#A32D2D" },
  "Approved":    { bg:"#E7F4EF", color:"#1F6F54" },
  "Locked":      { bg:"#EEEEF5", color:"#4A4A6A" },
};

// seeded so the grid opens with something meaningful rather than zeros
function seedValues() {
  const v = {};
  ACCOUNTS.forEach((a) => {
    if (a.kind !== "input" && a.kind !== "actual") return;
    // Per-account starting figures. Previously every staff-cost account was
    // seeded at 26,000 a month, which put an absurd recruitment and training
    // budget on every company and made the group look like it retained cost
    // when it does not.
    const base = a.code === "6030" ? 1500          // recruitment & training
               : a.code === "5000" ? 5400          // government and registry fees
               : a.code === "5010" ? 2600          // sub-contracted services
               : a.code === "7000" ? 6800 : a.code === "7010" ? 4200
               : a.code === "7020" ? 2900 : a.code === "7030" ? 3400
               : a.code === "7040" ? 1700 : a.code === "7050" ? 1250
               : a.group === "Revenue" ? 18000 + (Number(a.code) % 7) * 2200
               : 2500;
    MONTHS.forEach((m, i) => {
      const drift = 1 + (i * 0.006) + (((Number(a.code) + i) % 5) - 2) * 0.012;
      v[a.code + ":" + i] = Math.round((base * drift) / 50) * 50;
    });
  });
  return v;
}

const nf = (n) => n == null || n === "" ? "" : Number(n).toLocaleString("en-GB", { maximumFractionDigits: 0 });

// ── Carried over from the Budgets module this replaces, so nothing is lost.
const VARIANCE = [
  { line:"Retainer income",   budget:980000, actual:497000, variance:12000,  pct:"+2.5%",  status:"Favourable" },
  { line:"Ad hoc income",     budget:420000, actual:198000, variance:-8000,  pct:"-3.8%",  status:"Adverse"    },
  { line:"Specialist income", budget:700000, actual:372000, variance:18000,  pct:"+5.1%",  status:"Favourable" },
  { line:"Staff costs",       budget:860000, actual:428000, variance:6000,   pct:"+1.4%",  status:"Adverse"    },
  { line:"Office & premises", budget:180000, actual:88000,  variance:-4000,  pct:"-4.3%",  status:"Favourable" },
  { line:"IT & software",     budget:95000,  actual:52000,  variance:2000,   pct:"+4.0%",  status:"Adverse"    },
  { line:"Professional fees", budget:120000, actual:58000,  variance:-8000,  pct:"-12.1%", status:"Favourable" },
  { line:"Travel & expenses", budget:65000,  actual:28000,  variance:4000,   pct:"+16.7%", status:"Adverse"    },
];

const SERVICELINES = [
  { line:"Company administration", budget:680000, forecast:710000, actual:348000, margin:38 },
  { line:"Trust administration",   budget:520000, forecast:545000, actual:268000, margin:42 },
  { line:"Compliance services",    budget:310000, forecast:325000, actual:162000, margin:45 },
  { line:"Accounting & finance",   budget:280000, forecast:290000, actual:141000, margin:35 },
  { line:"Specialist — Yachting",  budget:180000, forecast:195000, actual:94000,  margin:52 },
  { line:"Specialist — Sports",    budget:130000, forecast:138000, actual:68000,  margin:48 },
];

const MONTHLY = [
  { month:"Apr", budget:160000, forecast:165000, actual:158000 },
  { month:"May", budget:163000, forecast:168000, actual:171000 },
  { month:"Jun", budget:165000, forecast:172000, actual:168000 },
  { month:"Jul", budget:168000, forecast:175000, actual:null },
  { month:"Aug", budget:162000, forecast:170000, actual:null },
  { month:"Sep", budget:170000, forecast:178000, actual:null },
];

const SCENARIOS_SEED = [
  { id:1, name:"FY26 Budget",            base:null,  status:"Approved",    owner:"Neil Kelly",  created:"2025-11-14", locked:true,  delta:0 },
  { id:2, name:"FY26 Forecast — Q1 roll",base:"FY26 Budget", status:"In progress", owner:"Neil Kelly", created:"2026-04-02", locked:false, delta:2.4 },
  { id:3, name:"FY26 Downside",          base:"FY26 Budget", status:"In progress", owner:"Andrew Morgan", created:"2026-05-19", locked:false, delta:-11.8 },
  { id:4, name:"FY26 Miami expansion",   base:"FY26 Budget", status:"Draft",       owner:"Andrew Morgan", created:"2026-06-30", locked:false, delta:6.1 },
];

export default function AffinityPlanning({ onNav, userName = "" }) {
  // ── Write layer plumbing ──────────────────────────────────────────────────
  const [wBusy, setWBusy] = useState(false);
  const [wMsg, setWMsg]   = useState("");
  const wRun = async (fn, okText) => {
    setWBusy(true); setWMsg("");
    try {
      const res = await fn();
      setWBusy(false);
      if (res && res.ok) { setWMsg(okText || "Saved."); return true; }
      if (res && res.live === false) { setWMsg("Not signed in — this cannot be saved yet."); return false; }
      setWMsg((res && res.error) || "That could not be saved.");
      return false;
    } catch (e) {
      setWBusy(false);
      setWMsg(String((e && e.message) || e));
      return false;
    }
  };

  // Copying an approved budget into a scenario. The copy carries all its lines,
  // so changing the scenario never touches the approved figures.
  const createScenario = async () => {
    if (!budgetId) { setWMsg("No budget is loaded to copy from."); return; }
    const name = window.prompt("Name for the new scenario:");
    if (!name) { setWMsg("Give the scenario a name."); return; }
    await wRun(() => planningScenarioCreate(budgetId, name), "Scenario created from the approved budget.");
  };

  const [view, setView]       = useState("input");   // input | workflow | scenarios
  const [entity, setEntity]   = useState(ENTITIES[1].ref);
  const [scenario, setScenario] = useState("FY26 Budget");
  const [values, setValues]   = useState(seedValues);
  const [undoStack, setUndo]  = useState([]);
  const [dirty, setDirty]     = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [sel, setSel]         = useState({ r:0, c:0 });
  const [editing, setEditing] = useState(null);   // "code:idx"
  const [draft, setDraft]     = useState("");
  const [drawer, setDrawer]   = useState(null);   // "comments" | "validation" | null
  const [comments, setComments] = useState({});
  const [state, setState]     = useState("In progress");
  const [history, setHistory] = useState([
    { at:"14/11/2025 09:20", who:"Neil Kelly", what:"Budget created from FY25 actuals" },
  ]);
  const gridRef = useRef(null);

  // ── Fee book, imported from recurring fees then amended here ──────────────
  const [fees, setFees] = useState([
    { id:1, client:"Meridian Holdings Ltd", service:"AA", desc:"Company administration", amount:1000, frequency:"M", ccy:"GBP", markup:true, status:"Recurring", billedBy:"AFG-IOM", account:"4000" },
    { id:2, client:"Meridian Holdings Ltd", service:"AB", desc:"Directorship",           amount:2000, frequency:"A", ccy:"GBP", markup:true, status:"Recurring", billedBy:"AFG-IOM", account:"4020" },
    { id:3, client:"Meridian Holdings Ltd", service:"AC", desc:"AEOI reporting",         amount:750,  frequency:"A", ccy:"GBP", markup:true, status:"Recurring", billedBy:"AFG-IOM", account:"4040" },
    { id:4, client:"Meridian Holdings Ltd", service:"AD", desc:"Tax compliance",         amount:300,  frequency:"A", ccy:"GBP", markup:true, status:"Recurring", billedBy:"AFG-IOM", account:"4040" },
    { id:5, client:"Azure Mediterranean Foundation", service:"AA", desc:"Administration", amount:2000, frequency:"M", ccy:"EUR", markup:true, status:"Recurring", billedBy:"AFG-MLT", account:"4000" },
    { id:6, client:"Azure Mediterranean Foundation", service:"AF", desc:"VAT returns",    amount:750,  frequency:"Q", ccy:"EUR", markup:true, status:"Recurring", billedBy:"AFG-MLT", account:"4040" },
    { id:7, client:"Thornbury Asset Co Ltd", service:"AA", desc:"Company administration", amount:1600, frequency:"A", ccy:"GBP", markup:true, status:"Lost", endsMonth:5, billedBy:"AFG-UK", account:"4000" },
    { id:8, client:"Kestrel Gaming Ltd (new)", service:"AA", desc:"Company administration", amount:2400, frequency:"M", ccy:"GBP", markup:true, status:"New business", startsMonth:9, sector:"eGaming", billedBy:"AFG-IOM", account:"4000" },
  ]);

  // ── Staff, imported from payroll then amended here ────────────────────────
  const [staff, setStaff] = useState([
    { id:1, name:"Roxy Sheeley",   dept:"Corporate Services", role:"Managing Director", entity:"AFG-IOM",    annualSalary:96000, changes:[], bonuses:[{ month:11, amount:12000 }] },
    { id:2, name:"Neil Kelly",     dept:"Finance",            role:"CFO",               entity:"AFG-FL",     annualSalary:88000, changes:[], bonuses:[{ month:11, amount:10000 }], recharges:[{ entity:"AFG-000", pct:100, onward:true }] },
    { id:3, name:"Colette Grisdale",dept:"Compliance",        role:"Head of Compliance", entity:"AFG-000",    annualSalary:72000, changes:[{ month:6, annualSalary:76000 }], bonuses:[], recharges:[{ entity:"AFG-IOM", pct:30 },{ entity:"AFG-MLT", pct:20 },{ entity:"AFG-CYM", pct:15 },{ entity:"AFG-UK", pct:15 }] },
    { id:4, name:"Joanne Fenech",  dept:"Corporate Services", role:"Director",          entity:"AFG-MLT",  annualSalary:68000, changes:[], bonuses:[] },
    { id:5, name:"Garry Crossan",  dept:"Corporate Services", role:"Director",          entity:"AFG-CYM", annualSalary:66000, changes:[], bonuses:[] },
    { id:6, name:"Administrator A",dept:"Corporate Services", role:"Administrator",     entity:"AFG-IOM",    annualSalary:34000, changes:[{ month:3, annualSalary:36000 }], bonuses:[] },
    { id:7, name:"Administrator B",dept:"Trust",              role:"Administrator",     entity:"AFG-UK",     annualSalary:32000, changes:[], bonuses:[], leaveMonth:5 },
    { id:8, name:"Trainee (planned)",dept:"Corporate Services",role:"Trainee",          entity:"AFG-FL",     annualSalary:24000, changes:[], bonuses:[], startMonth:8 },
    { id:9, name:"Group Financial Controller",dept:"Finance",   role:"Controller",        entity:"AFG-000",    annualSalary:64000, changes:[], bonuses:[] },
    { id:10,name:"Group IT Manager",dept:"IT",                  role:"IT Manager",        entity:"AFG-000",    annualSalary:58000, changes:[], bonuses:[] },
    { id:11,name:"Senior Administrator",dept:"Corporate Services",role:"Senior Administrator",entity:"AFG-IOM", annualSalary:46000, changes:[], bonuses:[], recharges:[{ entity:"AFG-000", pct:20 }] },
    { id:12,name:"Client Accountant", dept:"Finance",            role:"Accountant",        entity:"AFG-IOM",    annualSalary:42000, changes:[], bonuses:[], recharges:[{ entity:"AFG-000", pct:15 }] },
  ]);

  const [collectionDays, setCollectionDays] = useState(35);
  const [paymentDays, setPaymentDays]       = useState(30);
  const [stage, setStage]                   = useState("owner_md");
  const [rcOpen, setRcOpen]                 = useState(null);   // staff id whose recharges are open
  const [live, setLive]       = useState(false);   // true once real budget lines are loaded
  const [budgetId, setBudgetId] = useState(null);
  const [loadErr, setLoadErr] = useState(null);

  // Load the real budget when the database is there; otherwise stay on preview
  // figures and say so, rather than passing preview numbers off as live.
  useEffect(() => {
    let ok = true;
    budgetList().then(async (r) => {
      if (!ok || !r.live || !r.data || !r.data.length) return;
      const b = r.data[0];
      setBudgetId(b.id);
      const g = await budgetGrid(b.id);
      if (!ok || !g.live || !g.data) return;
      const pivot = pivotToGrid(g.data);
      if (Object.keys(pivot).length) { setValues(pivot); setLive(true); }
    }).catch((e) => ok && setLoadErr(String(e && e.message)));
    return () => { ok = false; };
  }, []);

  const ent = ENTITIES.find((e) => e.ref === entity) || ENTITIES[0];
  const locked = state === "Locked" || state === "Approved";

  // ── Values pulled from other tabs ─────────────────────────────────────────
  // Revenue comes from Fees & sales, staff costs from Staff, both filtered to
  // the employing / billing company selected above. These are read-only on the
  // grid: typing over a figure that another sheet supplies would be overwritten
  // silently, which is worse than not offering the cell at all.
  const linked = useMemo(() => {
    const out = {};
    const mine = fees.filter((f) => (f.billedBy || "AFG-IOM") === entity);
    mine.forEach((f) => {
      const code = f.account || "4000";
      const p = phaseFee(f, 2026);
      out[code] = out[code] || new Array(12).fill(0);
      for (let i = 0; i < 12; i++) out[code][i] += p.earned[i];   // P&L basis
    });
    const team = phaseHeadcount(
      staff.filter((p) => (p.entity || "AFG-IOM") === entity)
           .map((p) => ({ ...p, region: ENTITY_REGION[p.entity || "AFG-IOM"] || "IOM" }))
    );
    out["6000"] = team.salary; out["6010"] = team.social; out["6020"] = team.pension;
    out["6025"] = team.bonus;  out["6026"] = team.benefits;

    // recharges: a credit in the employing company, a debit in the receiver's,
    // each in its own budget currency
    const ccyMap = ENTITIES.reduce((a, e) => { a[e.ref] = e.ccy; return a; }, {});
    const zero = new Array(12).fill(0);
    const rc = computeRecharges(
      staff.map((p) => ({ ...p, region: ENTITY_REGION[p.entity || "AFG-IOM"] || "IOM" })),
      ccyMap
    )[entity] || { rechargedIn: zero, rechargedOut: zero, groupOnChargeIn: zero, groupOnChargeOut: zero };
    out["6040"] = rc.rechargedIn;
    out["6041"] = rc.groupOnChargeIn;
    out["6045"] = rc.rechargedOut.map((v) => -v);        // credits
    out["6046"] = rc.groupOnChargeOut.map((v) => -v);
    return out;
  }, [fees, staff, entity]);

  // ── derived values ────────────────────────────────────────────────────────
  const valueOf = useCallback((acc, i) => {
    if (acc.kind === "linked") {
      const series = linked[acc.code];
      return series ? Math.round(series[i]) : 0;
    }
    if (acc.kind === "calcPct") {
      const src = values[acc.of + ":" + i];
      return src == null ? 0 : Math.round(src * acc.pct);
    }
    const v = values[acc.code + ":" + i];
    return v == null ? 0 : Number(v);
  }, [values, linked]);

  const rowTotal = useCallback((acc) => MONTHS.reduce((s, _, i) => s + valueOf(acc, i), 0), [valueOf]);

  const groupTotal = useCallback((g, i) =>
    ACCOUNTS.filter((a) => a.group === g).reduce((s, a) => s + valueOf(a, i), 0), [valueOf]);

  const monthResult = useCallback((i) =>
    GROUPS.reduce((s, g) => s + SIGN[g] * groupTotal(g, i), 0), [groupTotal]);

  const yearResult = useMemo(() => MONTHS.reduce((s, _, i) => s + monthResult(i), 0), [monthResult]);
  const yearRevenue = useMemo(() => MONTHS.reduce((s, _, i) => s + groupTotal("Revenue", i), 0), [groupTotal]);

  // ── validation, the gate before submission ────────────────────────────────
  const validation = useMemo(() => {
    const issues = [];
    ACCOUNTS.filter((a) => a.kind === "input").forEach((a) => {
      const blanks = MONTHS.filter((_, i) => values[a.code + ":" + i] == null || values[a.code + ":" + i] === "").length;
      if (blanks === 12) issues.push({ level:"warn", msg:`${a.code} ${a.name} — no values entered for any period` });
      else if (blanks > 0) issues.push({ level:"warn", msg:`${a.code} ${a.name} — ${blanks} period${blanks>1?"s":""} blank` });
      const neg = MONTHS.filter((_, i) => Number(values[a.code + ":" + i]) < 0).length;
      if (neg) issues.push({ level:"error", msg:`${a.code} ${a.name} — ${neg} negative value${neg>1?"s":""}; use the opposite account instead` });
    });
    if (yearRevenue === 0) issues.push({ level:"error", msg:"No revenue budgeted for the year" });
    const margin = yearRevenue ? yearResult / yearRevenue : 0;
    if (margin < -0.05) issues.push({ level:"warn", msg:`Budgeted net margin is ${(margin*100).toFixed(1)}% — confirm this is intended` });
    return issues;
  }, [values, yearRevenue, yearResult]);

  const errors = validation.filter((v) => v.level === "error");

  // ── editing ───────────────────────────────────────────────────────────────
  const pushUndo = () => setUndo((u) => [...u.slice(-24), values]);

  const commit = (code, i, raw) => {
    const cleaned = String(raw).replace(/[^0-9.\-]/g, "");
    if (cleaned === "") { setEditing(null); return; }
    pushUndo();
    const amount = Math.round(Number(cleaned));
    setValues((v) => ({ ...v, [code + ":" + i]: amount }));
    setDirty(true);
    setEditing(null);
    if (live && budgetId) {
      const period = "2026-" + String(i + 1).padStart(2, "0");
      setBudgetCell(budgetId, code, period, amount).then((r) => {
        if (r.error) setLoadErr("Could not save " + code + " " + period + ": " + (r.error.message || r.error));
      });
    }
  };

  const undo = () => {
    setUndo((u) => {
      if (!u.length) return u;
      setValues(u[u.length - 1]);
      setDirty(true);
      return u.slice(0, -1);
    });
  };

  // spread a full-year figure evenly across the twelve periods
  const spread = (acc, annual) => {
    pushUndo();
    const per = Math.round(Number(annual) / 12);
    setValues((v) => {
      const n = { ...v };
      MONTHS.forEach((_, i) => { n[acc.code + ":" + i] = per; });
      return n;
    });
    setDirty(true);
  };

  // autosave — mirrors the spec's requirement, debounced
  useEffect(() => {
    if (!dirty) return;
    const t = setTimeout(() => {
      setSavedAt(new Date());
      setDirty(false);
    }, 1200);
    return () => clearTimeout(t);
  }, [dirty, values]);

  const editableRows = ACCOUNTS.filter((a) => a.kind === "input");   // excludes linked and formula rows

  // keyboard navigation across the grid
  const onKey = (e) => {
    if (editing) return;
    const max = editableRows.length - 1;
    let { r, c } = sel;
    if (e.key === "ArrowDown")  r = Math.min(max, r + 1);
    else if (e.key === "ArrowUp")   r = Math.max(0, r - 1);
    else if (e.key === "ArrowRight" || e.key === "Tab") { e.preventDefault(); c = c + 1 > 11 ? 0 : c + 1; if (c === 0) r = Math.min(max, r + 1); }
    else if (e.key === "ArrowLeft") c = Math.max(0, c - 1);
    else if (e.key === "Enter") { const a = editableRows[r]; setEditing(a.code + ":" + c); setDraft(String(values[a.code + ":" + c] ?? "")); return; }
    else if (/^[0-9]$/.test(e.key)) { const a = editableRows[r]; setEditing(a.code + ":" + c); setDraft(e.key); return; }
    else if (e.key === "Delete" || e.key === "Backspace") { const a = editableRows[r]; pushUndo(); setValues((v) => ({ ...v, [a.code + ":" + c]: 0 })); setDirty(true); return; }
    else return;
    e.preventDefault();
    setSel({ r, c });
  };

  // multi-cell paste, tab or comma separated, straight out of Excel
  const onPaste = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData("text");
    if (!text) return;
    e.preventDefault();
    const rows = text.trim().split(/\r?\n/).map((line) => line.split(/\t|,/));
    pushUndo();
    setValues((v) => {
      const n = { ...v };
      rows.forEach((cells, ri) => {
        const acc = editableRows[sel.r + ri];
        if (!acc) return;
        cells.forEach((cell, ci) => {
          const col = sel.c + ci;
          if (col > 11) return;
          const num = Number(String(cell).replace(/[^0-9.\-]/g, ""));
          if (!isNaN(num)) n[acc.code + ":" + col] = Math.round(num);
        });
      });
      return n;
    });
    setDirty(true);
  };

  const transition = (to, note) => {
    if (live && budgetId) {
      if (to === "Submitted") submitBudget(budgetId, userName || "unknown");
      if (to === "Approved")  approveBudget(budgetId, userName || "unknown");
    }
    setState(to);
    setHistory((h) => [...h, {
      at: new Date().toLocaleString("en-GB", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" }),
      who: userName || "Andrew Morgan",
      what: note,
    }]);
  };

  // ── shared styles ─────────────────────────────────────────────────────────
  const btn  = { padding:"6px 12px", fontSize:11.5, borderRadius:6, border:`0.5px solid ${LINE}`, background:"transparent", color:MUT, cursor:"pointer" };
  const btnP = { ...btn, background:CY, color:"#fff", border:`0.5px solid ${CY}`, fontWeight:600 };
  const panelBox  = { background:CARD, border:`0.5px solid ${LINE}`, borderRadius:9, overflow:"hidden" };
  const panelNote = { background:"#EFF7F8", border:`0.5px solid #cfe7ea`, borderRadius:8, padding:"10px 13px",
                      fontSize:11.5, color:"#0d5c66", lineHeight:1.7 };
  const thS = { padding:"8px 12px", textAlign:"left", fontSize:10, fontWeight:600, color:MUT, textTransform:"uppercase",
                letterSpacing:"0.4px", background:SUBTLE, borderBottom:`0.5px solid ${LINE}`, whiteSpace:"nowrap" };
  const tdS = { padding:"7px 12px", fontSize:12, borderBottom:"none", whiteSpace:"nowrap" };
  const numS = { ...tdS, textAlign:"right", fontVariantNumeric:"tabular-nums" };
  const selS = { height:26, padding:"0 6px", fontSize:11, border:`0.5px solid ${LINE}`, borderRadius:5,
                 background:CARD, color:INK, boxSizing:"border-box" };
  const cellBase = { padding:"0", borderRight:`0.5px solid ${LINE}`, borderBottom:`0.5px solid ${LINE}`, height:28, fontSize:12,
                     textAlign:"right", fontVariantNumeric:"tabular-nums", whiteSpace:"nowrap" };
  const stickyHead = { position:"sticky", top:0, zIndex:3, background:SUBTLE };
  const stickyCol  = { position:"sticky", left:0, zIndex:2, background:CARD, borderRight:`0.5px solid ${LINE}`, textAlign:"left" };

  const Badge = ({ label }) => {
    const st = STATE_STYLE[label] || STATE_STYLE["Not started"];
    return <span style={{ background:st.bg, color:st.color, borderRadius:20, padding:"3px 10px", fontSize:10.5, fontWeight:700 }}>{label}</span>;
  };

  return (
    <div style={{ fontFamily:FONT, background:PAGE, minHeight:"100vh", color:INK }} onPaste={onPaste}>
      {wMsg && (
        <div style={{ margin:"0 20px 10px", padding:"9px 12px", borderRadius:7, fontSize:11.5,
                      lineHeight:1.6, border:"0.5px solid",
                      background: /Saved|Submitted|Approved|Returned|Posted|Issued|Created|Prepared|Chased|Suspended|Started|Added/.test(wMsg) ? "#E7F4EF" : "#FCEBEB",
                      borderColor: /Saved|Submitted|Approved|Returned|Posted|Issued|Created|Prepared|Chased|Suspended|Started|Added/.test(wMsg) ? "#bfe0d2" : "#f0c9c9",
                      color: /Saved|Submitted|Approved|Returned|Posted|Issued|Created|Prepared|Chased|Suspended|Started|Added/.test(wMsg) ? "#1F6F54" : "#A32D2D" }}>
          {wMsg}
        </div>
      )}


      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 22px", background:CARD, borderBottom:`0.5px solid ${LINE}`, flexWrap:"wrap" }}>
        <h2 style={{ margin:0, fontSize:18, fontWeight:500, color:NAVY }}>Planning</h2>
        <div style={{ display:"flex", border:`0.5px solid ${LINE}`, borderRadius:6, overflow:"hidden" }}>
          {[["input","Budget input"],["fees","Fees & sales"],["staff","Staff"],["balance","Balance sheet & cash"],["workflow","Workflow"],["scenarios","Scenarios"],["variance","Variance & analysis"]].map(([v,l])=>(
            <button key={v} onClick={()=>setView(v)}
              style={{ border:"none", cursor:"pointer", fontSize:11.5, padding:"6px 13px", fontWeight:view===v?600:400,
                       background:view===v?CY:CARD, color:view===v?"#fff":MUT }}>{l}</button>
          ))}
        </div>
        <span style={{ fontSize:10.5, borderRadius:20, padding:"3px 10px",
          color: live?"#1F6F54":"#B08A3E", background: live?"#E7F4EF":"#FDF4DC" }}>
          {live ? "Live data" : "Preview data"}
        </span>
        {loadErr && <span style={{ fontSize:10.5, color:NEG }} title={loadErr}>save issue</span>}
      </div>

      {/* Persistent filter bar */}
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 22px", background:CARD, borderBottom:`0.5px solid ${LINE}`, flexWrap:"wrap" }}>
        <span style={{ fontSize:10, fontWeight:600, color:"#aaa", textTransform:"uppercase", letterSpacing:"0.5px" }}>Entity</span>
        <select value={entity} onChange={(e)=>setEntity(e.target.value)}
          style={{ height:30, padding:"0 8px", fontSize:11.5, border:`0.5px solid ${LINE}`, borderRadius:6, background:CARD, color:INK, minWidth:230 }}>
          {ENTITIES.map((e)=><option key={e.ref} value={e.ref}>{e.name}</option>)}
        </select>
        <span style={{ fontSize:10, fontWeight:600, color:"#aaa", textTransform:"uppercase", letterSpacing:"0.5px", marginLeft:6 }}>Scenario</span>
        <select value={scenario} onChange={(e)=>setScenario(e.target.value)}
          style={{ height:30, padding:"0 8px", fontSize:11.5, border:`0.5px solid ${LINE}`, borderRadius:6, background:CARD, color:INK }}>
          {SCENARIOS_SEED.map((s)=><option key={s.id}>{s.name}</option>)}
        </select>
        <span style={{ fontSize:11, color:MUT }}>FY26 · {ent.ccy}</span>
        <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:8 }}>
          <Badge label={state} />
          <span style={{ fontSize:10.5, color:MUT }}>
            {dirty ? "Saving…" : savedAt ? "Saved " + savedAt.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}) : "No changes"}
          </span>
        </div>
      </div>

      {/* ══════════════ BUDGET INPUT GRID ══════════════ */}
      {view === "input" && (
        <div style={{ display:"flex", alignItems:"flex-start" }}>
          <div style={{ flex:1, minWidth:0 }}>

            {/* Grid toolbar */}
            <div style={{ display:"flex", alignItems:"center", gap:7, padding:"9px 22px", flexWrap:"wrap" }}>
              <button style={btn} onClick={undo} disabled={!undoStack.length}
                title="Undo the last change">↶ Undo</button>
              <button style={btn} onClick={()=>setDrawer(drawer==="validation"?null:"validation")}>
                Validation {errors.length ? <span style={{ color:NEG, fontWeight:700 }}>· {errors.length}</span>
                                          : <span style={{ color:POS, fontWeight:700 }}>· clear</span>}
              </button>
              <button style={btn} onClick={()=>setDrawer(drawer==="comments"?null:"comments")}>
                Comments {Object.keys(comments).length>0 && <strong>· {Object.keys(comments).length}</strong>}
              </button>
              <span style={{ fontSize:10.5, color:MUT, marginLeft:6 }}>
                Click a cell then type · arrows to move · Enter to edit · paste a block straight from Excel
              </span>
              <div style={{ marginLeft:"auto", display:"flex", gap:7 }}>
                {(state==="In progress"||state==="Returned") && (
                  <button style={{ ...btnP, opacity: errors.length?0.5:1, cursor: errors.length?"not-allowed":"pointer" }}
                    disabled={!!errors.length}
                    title={errors.length?"Resolve validation errors before submitting":"Submit for approval"}
                    onClick={()=>transition("Submitted","Submitted for approval")}>Submit for approval ↗</button>
                )}
                {state==="Submitted" && (<>
                  <button style={btn} onClick={()=>transition("Returned","Returned for revision")}>Return</button>
                  <button style={btnP} onClick={()=>transition("Approved","Budget approved")}>Approve ✓</button>
                </>)}
                {state==="Approved" && <button style={btn} onClick={()=>transition("Locked","Period locked")}>Lock period</button>}
                {state==="Locked" && <button style={btn} onClick={()=>transition("In progress","Period reopened — reason recorded")}>Reopen</button>}
              </div>
            </div>

            {/* The grid */}
            <div ref={gridRef} tabIndex={0} onKeyDown={onKey}
              style={{ margin:"0 22px 22px", background:CARD, border:`0.5px solid ${LINE}`, borderRadius:9,
                       overflow:"auto", maxHeight:"64vh", outline:"none" }}>
              <table style={{ borderCollapse:"separate", borderSpacing:0, minWidth:1180 }}>
                <thead>
                  <tr>
                    <th style={{ ...cellBase, ...stickyHead, ...stickyCol, zIndex:4, minWidth:260, padding:"0 12px",
                                 fontSize:10, fontWeight:600, color:MUT, textTransform:"uppercase", letterSpacing:"0.4px" }}>Account</th>
                    {MONTHS.map((m)=>(
                      <th key={m} style={{ ...cellBase, ...stickyHead, minWidth:74, padding:"0 8px",
                                           fontSize:10, fontWeight:600, color:MUT, textTransform:"uppercase", letterSpacing:"0.4px" }}>{m}</th>
                    ))}
                    <th style={{ ...cellBase, ...stickyHead, minWidth:92, padding:"0 10px", fontSize:10, fontWeight:700,
                                 color:NAVY, textTransform:"uppercase", letterSpacing:"0.4px", background:"#EFF7F8" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {GROUPS.map((g)=>(
                    <>
                      <tr key={g}>
                        <td colSpan={14} style={{ ...cellBase, ...stickyCol, textAlign:"left", padding:"0 12px",
                                                  background:SUBTLE, fontWeight:700, fontSize:11, color:NAVY,
                                                  textTransform:"uppercase", letterSpacing:"0.4px" }}>{g}</td>
                      </tr>
                      {ACCOUNTS.filter((a)=>a.group===g).map((a)=>{
                        const rowIdx = editableRows.indexOf(a);
                        return (
                          <tr key={a.code}>
                            <td style={{ ...cellBase, ...stickyCol, padding:"0 12px", fontSize:12 }}>
                              <span style={{ color:MUT, fontSize:10.5, marginRight:8 }}>{a.code}</span>{a.name}
                              {a.kind==="calcPct" && <span style={{ marginLeft:6, fontSize:9, color:"#7B4F1D", background:"#FDF4DC", borderRadius:8, padding:"1px 5px" }}>FORMULA</span>}
                              {a.kind==="actual"  && <span style={{ marginLeft:6, fontSize:9, color:"#1F6F54", background:"#E7F4EF", borderRadius:8, padding:"1px 5px" }}>ACTUAL</span>}
                              {a.kind==="linked"  && (
                                <button onClick={()=>setView(a.src==="fees"?"fees":"staff")}
                                  title={"Pulled from the "+(a.src==="fees"?"Fees & sales":"Staff")+" tab — change it there, not here"}
                                  style={{ marginLeft:6, fontSize:9, color:"#274690", background:"#EAF0FB", border:"none",
                                           borderRadius:8, padding:"1px 6px", cursor:"pointer" }}>
                                  FROM {a.src==="fees" ? "FEES" : "STAFF"} ↗
                                </button>
                              )}
                              {a.kind==="input" && (
                                <button title="Spread a full-year figure evenly across the periods"
                                  onClick={()=>{ const t=window.prompt("Annual figure to spread evenly across 12 periods:", String(rowTotal(a))); if(t!=null) spread(a,t); }}
                                  style={{ marginLeft:8, border:"none", background:"none", cursor:"pointer", color:CY, fontSize:10 }}>spread</button>
                              )}
                            </td>
                            {MONTHS.map((m,i)=>{
                              const key = a.code+":"+i;
                              const isEditing = editing===key;
                              const isSel = rowIdx>-1 && sel.r===rowIdx && sel.c===i;
                              const editable = a.kind==="input" && !locked;
                              const bg = locked ? "#F4F4F6"
                                       : a.kind==="linked"  ? "#EFF3FA"
                                       : a.kind==="calcPct" ? "#FBF7EC"
                                       : a.kind==="actual"  ? "#F1F7F4"
                                       : isSel ? "rgba(0,196,204,0.12)" : "#FCFEFF";
                              return (
                                <td key={m}
                                  onClick={()=>{ if(rowIdx>-1) setSel({r:rowIdx,c:i}); if(editable){ setEditing(key); setDraft(String(values[key] ?? "")); } }}
                                  style={{ ...cellBase, background:bg, cursor:editable?"cell":"default",
                                           outline:isSel?`1.5px solid ${CY}`:"none", outlineOffset:"-1.5px" }}>
                                  {isEditing ? (
                                    <input autoFocus value={draft}
                                      onChange={(e)=>setDraft(e.target.value)}
                                      onBlur={()=>commit(a.code,i,draft)}
                                      onKeyDown={(e)=>{
                                        if(e.key==="Enter"){ commit(a.code,i,draft); setSel({r:rowIdx,c:i}); gridRef.current&&gridRef.current.focus(); }
                                        if(e.key==="Escape"){ setEditing(null); }
                                        if(e.key==="Tab"){ e.preventDefault(); commit(a.code,i,draft); setSel({r:rowIdx,c:Math.min(11,i+1)}); gridRef.current&&gridRef.current.focus(); }
                                      }}
                                      style={{ width:"100%", height:"100%", border:"none", outline:"none", textAlign:"right",
                                               padding:"0 8px", fontSize:12, fontFamily:FONT, background:"#fff",
                                               fontVariantNumeric:"tabular-nums", boxSizing:"border-box" }} />
                                  ) : (
                                    <span style={{ display:"block", padding:"0 8px", lineHeight:"28px",
                                                   color: a.kind==="input" ? INK : MUT }}>{nf(valueOf(a,i))}</span>
                                  )}
                                </td>
                              );
                            })}
                            <td style={{ ...cellBase, padding:"0 10px", fontWeight:600, background:"#EFF7F8" }}>
                              <span style={{ lineHeight:"28px" }}>{nf(rowTotal(a))}</span>
                            </td>
                          </tr>
                        );
                      })}
                      <tr key={g+"-sub"}>
                        <td style={{ ...cellBase, ...stickyCol, padding:"0 12px", fontWeight:700, background:"#F3F6F9" }}>
                          <span style={{ lineHeight:"28px" }}>Total {g.toLowerCase()}</span>
                        </td>
                        {MONTHS.map((m,i)=>(
                          <td key={m} style={{ ...cellBase, fontWeight:700, background:"#F3F6F9" }}>
                            <span style={{ display:"block", padding:"0 8px", lineHeight:"28px" }}>{nf(groupTotal(g,i))}</span>
                          </td>
                        ))}
                        <td style={{ ...cellBase, padding:"0 10px", fontWeight:700, background:"#E6EFF1" }}>
                          <span style={{ lineHeight:"28px" }}>{nf(MONTHS.reduce((s,_,i)=>s+groupTotal(g,i),0))}</span>
                        </td>
                      </tr>
                    </>
                  ))}
                  <tr>
                    <td style={{ ...cellBase, ...stickyCol, padding:"0 12px", fontWeight:700, background:NAVY, color:"#fff" }}>
                      <span style={{ lineHeight:"28px" }}>Net result</span>
                    </td>
                    {MONTHS.map((m,i)=>{
                      const v = monthResult(i);
                      return (
                        <td key={m} style={{ ...cellBase, fontWeight:700, background:NAVY, color: v<0 ? "#FF9B9B" : "#8FE9ED" }}>
                          <span style={{ display:"block", padding:"0 8px", lineHeight:"28px" }}>{nf(v)}</span>
                        </td>
                      );
                    })}
                    <td style={{ ...cellBase, padding:"0 10px", fontWeight:700, background:"#000B26", color: yearResult<0 ? "#FF9B9B" : "#8FE9ED" }}>
                      <span style={{ lineHeight:"28px" }}>{nf(yearResult)}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Right drawer — validation and comments */}
          {drawer && (
            <div style={{ width:300, flexShrink:0, background:CARD, borderLeft:`0.5px solid ${LINE}`, minHeight:"64vh", padding:"14px 16px" }}>
              <div style={{ display:"flex", alignItems:"center", marginBottom:10 }}>
                <div style={{ fontSize:12.5, fontWeight:600, color:NAVY }}>{drawer==="validation"?"Validation":"Comments"}</div>
                <button onClick={()=>setDrawer(null)} style={{ marginLeft:"auto", border:"none", background:"none", cursor:"pointer", color:"#aaa" }}>✕</button>
              </div>
              {drawer==="validation" ? (
                validation.length===0
                  ? <div style={{ fontSize:11.5, color:POS }}>✓ No issues. Ready to submit.</div>
                  : validation.map((v,i)=>(
                      <div key={i} style={{ fontSize:11, lineHeight:1.6, padding:"7px 9px", marginBottom:6, borderRadius:6,
                                            background: v.level==="error"?"#FCEBEB":"#FDF4DC",
                                            color: v.level==="error"?"#A32D2D":"#7B4F1D" }}>
                        <strong>{v.level==="error"?"Error":"Check"}</strong> · {v.msg}
                      </div>
                    ))
              ) : (
                <>
                  <div style={{ fontSize:11, color:MUT, lineHeight:1.6, marginBottom:9 }}>
                    Attach an explanation to the selected cell. Approvers see these alongside the figure.
                  </div>
                  <div style={{ fontSize:11, fontWeight:600, marginBottom:6 }}>
                    {editableRows[sel.r] ? editableRows[sel.r].name + " · " + MONTHS[sel.c] : "No cell selected"}
                  </div>
                  <textarea rows={4} placeholder="Explain a movement or assumption…"
                    value={comments[(editableRows[sel.r]||{}).code + ":" + sel.c] || ""}
                    onChange={(e)=>setComments((c)=>({ ...c, [(editableRows[sel.r]||{}).code + ":" + sel.c]: e.target.value }))}
                    style={{ width:"100%", fontSize:11.5, fontFamily:FONT, border:`0.5px solid ${LINE}`, borderRadius:6,
                             padding:"7px 9px", resize:"vertical", boxSizing:"border-box", background:CARD, color:INK }} />
                  {Object.keys(comments).filter((k)=>comments[k]).length>0 && (
                    <div style={{ marginTop:12 }}>
                      <div style={{ fontSize:10, fontWeight:600, color:"#aaa", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:6 }}>All comments</div>
                      {Object.keys(comments).filter((k)=>comments[k]).map((k)=>{
                        const [code,idx]=k.split(":");
                        const acc=ACCOUNTS.find((a)=>a.code===code);
                        return (
                          <div key={k} style={{ fontSize:10.5, color:MUT, borderTop:`0.5px solid ${LINE}`, padding:"6px 0", lineHeight:1.5 }}>
                            <strong style={{ color:INK }}>{acc?acc.name:code} · {MONTHS[idx]}</strong><br/>{comments[k]}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ══════════════ FEES & SALES ══════════════ */}
      {view === "fees" && (() => {
        const mineF = fees.filter((f) => (f.billedBy || "AFG-IOM") === entity);
        const phased = mineF.map((f) => ({ f, p: phaseFee(f, 2026) }));
        const totals = phaseFees(mineF, 2026);
        const annualInv = totals.invoiced.reduce((a,b)=>a+b,0);
        const annualErn = totals.earned.reduce((a,b)=>a+b,0);
        const setFee = (id, patch) => setFees((rows)=>rows.map((r)=>r.id===id?{...r,...patch}:r));
        return (
          <div style={{ padding:"14px 22px 60px" }}>
            <div style={{ ...panelNote }}>
              Showing the <strong>{mineF.length}</strong> {mineF.length===1?"fee":"fees"} billed by <strong>{ent.name}</strong>.
              This is what feeds that company's revenue on the budget front sheet, which is why those rows are locked
              there. Amend an amount here and both the billing profile and the revenue recognised move with it. A fee marked <strong>Lost</strong> stops in the
              month given; <strong>New business</strong> starts in its month and carries the client's sector for reporting.
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:10, margin:"12px 0 14px" }}>
              {[["Fees in the book", String(fees.length), NAVY],
                ["Annual billed", "£"+nf(annualInv), CY],
                ["Annual earned", "£"+nf(annualErn), NAVY],
                ["Deferred at any point", "£"+nf(Math.max(...totals.invoiced.map((v,i)=>
                    totals.invoiced.slice(0,i+1).reduce((a,b)=>a+b,0) - totals.earned.slice(0,i+1).reduce((a,b)=>a+b,0)))), AMBER],
                ["Lost / new this year", fees.filter(f=>f.status==="Lost").length+" / "+fees.filter(f=>f.status==="New business").length, MUT],
              ].map(([l,v,c])=>(
                <div key={l} style={{ background:CARD, border:`0.5px solid ${LINE}`, borderRadius:9, padding:"11px 13px" }}>
                  <div style={{ fontSize:10.5, color:MUT, marginBottom:4 }}>{l}</div>
                  <div style={{ fontSize:17, fontWeight:700, color:c, fontVariantNumeric:"tabular-nums" }}>{v}</div>
                </div>
              ))}
            </div>

            <div style={{ ...panelBox, overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:1100 }}>
                <thead><tr>
                  {["Client","Billed by","Service","Description","Freq","Amount","Ccy","Uplift","Status","From","To","Billed FY","Earned FY"].map((h)=>(
                    <th key={h} style={thS}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {phased.map(({f,p})=>(
                    <tr key={f.id} style={{ borderBottom:`0.5px solid ${LINE}`,
                        background: f.status==="Lost" ? "#FFF7F7" : f.status==="New business" ? "#F5FBF7" : "transparent" }}>
                      <td style={tdS}>{f.client}</td>
                      <td style={tdS}>
                        <select value={f.billedBy||"AFG-IOM"} onChange={(e)=>setFee(f.id,{billedBy:e.target.value})} style={selS}
                          title="Which Affinity company bills this fee. The revenue rolls up to that company's budget.">
                          {ENTITIES.map((x)=><option key={x.ref} value={x.ref}>{x.name}</option>)}
                        </select>
                      </td>
                      <td style={{ ...tdS, fontFamily:"ui-monospace,monospace", fontSize:11 }}>{f.service}</td>
                      <td style={tdS}>{f.desc}</td>
                      <td style={tdS}>
                        <select value={f.frequency} onChange={(e)=>setFee(f.id,{frequency:e.target.value})} style={selS}>
                          {FREQUENCIES.map((x)=><option key={x.code} value={x.code}>{x.label}</option>)}
                        </select>
                      </td>
                      <td style={tdS}>
                        <input type="number" value={f.amount} onChange={(e)=>setFee(f.id,{amount:Number(e.target.value)||0})}
                          style={{ ...selS, width:88, textAlign:"right" }} />
                      </td>
                      <td style={{ ...tdS, color:MUT }}>{f.ccy}</td>
                      <td style={{ ...tdS, textAlign:"center" }}>
                        <input type="checkbox" checked={!!f.markup} onChange={()=>setFee(f.id,{markup:!f.markup})}
                          title="Apply the 5% annual uplift" style={{ width:14, height:14, cursor:"pointer" }} />
                      </td>
                      <td style={tdS}>
                        <span style={{ fontSize:10, fontWeight:700, borderRadius:20, padding:"2px 8px",
                          background: f.status==="Lost"?"#FCEBEB":f.status==="New business"?"#E7F4EF":"#EAF0FB",
                          color: f.status==="Lost"?"#A32D2D":f.status==="New business"?"#1F6F54":"#274690" }}>{f.status}</span>
                        {f.sector && <span style={{ marginLeft:5, fontSize:9.5, color:MUT }}>{f.sector}</span>}
                      </td>
                      <td style={tdS}>
                        <select value={f.startsMonth ?? 0} onChange={(e)=>setFee(f.id,{startsMonth:Number(e.target.value)})} style={selS}>
                          {MONTHS.map((m,i)=><option key={m} value={i}>{m}</option>)}
                        </select>
                      </td>
                      <td style={tdS}>
                        <select value={f.endsMonth ?? 11} onChange={(e)=>setFee(f.id,{endsMonth:Number(e.target.value)})} style={selS}>
                          {MONTHS.map((m,i)=><option key={m} value={i}>{m}</option>)}
                        </select>
                      </td>
                      <td style={numS}>{nf(p.invoiced.reduce((a,b)=>a+b,0))}</td>
                      <td style={numS}>{nf(p.earned.reduce((a,b)=>a+b,0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ fontSize:12.5, fontWeight:600, color:NAVY, margin:"18px 0 8px" }}>
              Billed against earned, by month — the difference is deferred income
            </div>
            <div style={{ ...panelBox, overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:900 }}>
                <thead><tr><th style={{ ...thS, minWidth:150 }}>Basis</th>
                  {MONTHS.map((m)=><th key={m} style={{ ...thS, textAlign:"right" }}>{m}</th>)}
                  <th style={{ ...thS, textAlign:"right" }}>Year</th></tr></thead>
                <tbody>
                  {[["Invoiced (billing)", totals.invoiced, NAVY],
                    ["Earned (P&L)", totals.earned, CY]].map(([l,arr,c])=>(
                    <tr key={l} style={{ borderBottom:`0.5px solid ${LINE}` }}>
                      <td style={{ ...tdS, fontWeight:600, color:c }}>{l}</td>
                      {arr.map((v,i)=><td key={i} style={numS}>{nf(v)}</td>)}
                      <td style={{ ...numS, fontWeight:700 }}>{nf(arr.reduce((a,b)=>a+b,0))}</td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ ...tdS, fontWeight:700, background:SUBTLE }}>Difference</td>
                    {totals.invoiced.map((v,i)=>(
                      <td key={i} style={{ ...numS, background:SUBTLE, fontWeight:600,
                        color: Math.abs(v-totals.earned[i])<0.01?MUT:AMBER }}>
                        {nf(v-totals.earned[i])}
                      </td>
                    ))}
                    <td style={{ ...numS, background:SUBTLE, fontWeight:700 }}>
                      {nf(totals.invoiced.reduce((a,b)=>a+b,0)-totals.earned.reduce((a,b)=>a+b,0))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ══════════════ STAFF ══════════════ */}
      {view === "staff" && (() => {
        const withRegion = (p) => ({ ...p, region: ENTITY_REGION[p.entity || "AFG-IOM"] || "IOM" });
        const mine = staff.filter((p) => (p.entity || "AFG-IOM") === entity).map(withRegion);
        const team = phaseHeadcount(mine);
        const groupTeam = phaseHeadcount(staff.map(withRegion));
        const setP = (id, patch) => setStaff((rows)=>rows.map((r)=>r.id===id?{...r,...patch}:r));
        return (
          <div style={{ padding:"14px 22px 60px" }}>
            <div style={panelNote}>
              Showing the <strong>{mine.length}</strong> {mine.length===1?"person":"people"} employed by <strong>{ent.name}</strong>.
              Their cost is what feeds that company's budget front sheet — change the employing company and the cost
              moves with them. Group-wide there are {staff.length} staff costing £{nf(groupTeam.total.reduce((a,b)=>a+b,0))} for the year.
              <div style={{ marginTop:6 }}>
                A pay change and the month it applies from recalculates gross, employer social and pension from that
                month. A leaver's date stops their cost in the right month. Healthcare, wellness and cinema follow
                headcount automatically. Employer rates follow the employing company's jurisdiction.
              </div>
              <div style={{ marginTop:6 }}>
                <strong>Recharges</strong> (the blue columns): where someone works across companies, name the company
                and the percentage. The employing company keeps the balance and credits the rest out; the receiving
                company picks it up in its own budget currency, converted at the planning rate
                (EUR {BUDGET_FX.EUR}, USD {BUDGET_FX.USD} to the pound). Both sides appear on the front sheet as
                <em> Staff recharged in</em> and <em>Staff recharged out</em>.
                {staff.some((p)=>!rechargeSummary(p).valid) && (
                  <span style={{ color:NEG, fontWeight:600 }}> One or more people are recharged above 100% — check the flagged rows.</span>
                )}
              </div>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))", gap:10, margin:"12px 0 14px" }}>
              {[["Headcount at Jan", String(team.heads[0]), NAVY],
                ["Headcount at Dec", String(team.heads[11]), NAVY],
                ["Salaries FY", "£"+nf(team.salary.reduce((a,b)=>a+b,0)), CY],
                ["On-costs FY", "£"+nf(team.social.reduce((a,b)=>a+b,0)+team.pension.reduce((a,b)=>a+b,0)), MUT],
                ["Total staff cost FY", "£"+nf(team.total.reduce((a,b)=>a+b,0)), NAVY],
              ].map(([l,v,c])=>(
                <div key={l} style={{ background:CARD, border:`0.5px solid ${LINE}`, borderRadius:9, padding:"11px 13px" }}>
                  <div style={{ fontSize:10.5, color:MUT, marginBottom:4 }}>{l}</div>
                  <div style={{ fontSize:17, fontWeight:700, color:c, fontVariantNumeric:"tabular-nums" }}>{v}</div>
                </div>
              ))}
            </div>

            <div style={{ ...panelBox, overflowX:"auto", marginBottom:18 }}>
              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:1050 }}>
                <thead><tr>
                  {["Name","Employing company","Department","Role","Payroll region","Opening salary","Change","From","Bonus","Starts","Leaves","Cost FY","Recharged to","Retained","Recharged out",""].map((h,hi)=>(
                    <th key={h} style={thS}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {staff.filter((p)=>(p.entity||"AFG-IOM")===entity).map((p)=>{
                    const r = phaseHeadcount([withRegion(p)]);
                    const chg = (p.changes||[])[0];
                    const bon = (p.bonuses||[])[0];
                    return (
                      <tr key={p.id} style={{ borderBottom:`0.5px solid ${LINE}`,
                          background: p.leaveMonth!=null ? "#FFF7F7" : p.startMonth!=null ? "#F5FBF7" : "transparent" }}>
                        <td style={{ ...tdS, fontWeight:600 }}>{p.name}</td>
                        <td style={tdS}>
                          <select value={p.entity||"AFG-IOM"} onChange={(e)=>setP(p.id,{entity:e.target.value})} style={selS}
                            title="Which Affinity company employs this person. Their cost rolls up to that company's budget.">
                            {ENTITIES.map((x)=><option key={x.ref} value={x.ref}>{x.name}</option>)}
                          </select>
                        </td>
                        <td style={tdS}>{p.dept}</td>
                        <td style={{ ...tdS, color:MUT }}>{p.role}</td>
                        <td style={tdS}>
                          {/* Payroll region follows the employing company rather than being
                              set separately — the two must never disagree. */}
                          <span style={{ fontSize:10.5, color:"#274690", background:"#EAF0FB", borderRadius:8, padding:"2px 8px" }}
                            title="Follows the employing company. Employer social and pension use this region's rates and ceilings.">
                            {(ONCOSTS_BY_REGION[ENTITY_REGION[p.entity||"AFG-IOM"]||"IOM"]||{}).label}
                          </span>
                        </td>
                        <td style={tdS}>
                          <input type="number" value={p.annualSalary}
                            onChange={(e)=>setP(p.id,{annualSalary:Number(e.target.value)||0})}
                            style={{ ...selS, width:96, textAlign:"right" }} />
                        </td>
                        <td style={tdS}>
                          <input type="number" value={chg?chg.annualSalary:""} placeholder="—"
                            onChange={(e)=>{ const v=Number(e.target.value)||0;
                              setP(p.id,{changes: v? [{ month: chg?chg.month:6, annualSalary:v }] : [] }); }}
                            style={{ ...selS, width:96, textAlign:"right" }} />
                        </td>
                        <td style={tdS}>
                          <select value={chg?chg.month:6} disabled={!chg}
                            onChange={(e)=>setP(p.id,{changes:[{ month:Number(e.target.value), annualSalary:chg.annualSalary }]})}
                            style={selS}>
                            {MONTHS.map((m,i)=><option key={m} value={i}>{m}</option>)}
                          </select>
                        </td>
                        <td style={tdS}>
                          <input type="number" value={bon?bon.amount:""} placeholder="—"
                            onChange={(e)=>{ const v=Number(e.target.value)||0;
                              setP(p.id,{bonuses: v? [{ month: bon?bon.month:11, amount:v }] : [] }); }}
                            style={{ ...selS, width:88, textAlign:"right" }} />
                        </td>
                        <td style={{ ...tdS, color:MUT, fontSize:11 }}>{p.startMonth!=null?MONTHS[p.startMonth]:"—"}</td>
                        <td style={{ ...tdS, color: p.leaveMonth!=null?NEG:MUT, fontSize:11 }}>{p.leaveMonth!=null?MONTHS[p.leaveMonth]:"—"}</td>
                        <td style={{ ...numS, fontWeight:600 }}>{nf(r.total.reduce((a,b)=>a+b,0))}</td>
                        {(() => {
                          const sum = rechargeSummary(p);
                          const ccyMap = ENTITIES.reduce((a,e)=>{a[e.ref]=e.ccy;return a;},{});
                          const rc = computeRecharges([{ ...p, region: ENTITY_REGION[p.entity||"AFG-IOM"]||"IOM" }], ccyMap)[p.entity||"AFG-IOM"];
                          const outFY = rc ? rc.rechargedOut.reduce((a,b)=>a+b,0) : 0;
                          return [
                            <td key="tg" style={{ ...tdS, background:"#FAFCFF" }}>
                              {sum.targets
                                ? <span style={{ fontSize:11 }}>{sum.targets} {sum.targets===1?"company":"companies"} · {sum.pct}%</span>
                                : <span style={{ fontSize:11, color:MUT }}>—</span>}
                            </td>,
                            <td key="ret" style={{ ...numS, background:"#FAFCFF",
                                 color: sum.valid ? (sum.pct?NAVY:MUT) : NEG, fontWeight: sum.pct?600:400 }}
                                 title={sum.warning || "Share of cost kept by the employing company"}>
                              {sum.retained}%{!sum.valid && " ⚠"}
                            </td>,
                            <td key="out" style={{ ...numS, background:"#FAFCFF", fontWeight:600, color: outFY?NEG:MUT }}>
                              {outFY ? "("+nf(outFY)+")" : "—"}
                            </td>,
                            <td key="ed" style={{ ...tdS, background:"#FAFCFF" }}>
                              <button style={{ ...btn, padding:"2px 8px", fontSize:10 }}
                                onClick={()=>setRcOpen(rcOpen===p.id?null:p.id)}>
                                {rcOpen===p.id ? "Close" : "Recharges ▾"}
                              </button>
                            </td>,
                          ];
                        })()}
                      </tr>
                    );
                  })}
                  {/* Up to six recharge targets per person. Twelve columns would be
                      unreadable, so the detail opens under the row instead. */}
                  {staff.filter((p)=>(p.entity||"AFG-IOM")===entity && rcOpen===p.id).map((p)=>{
                    const sum = rechargeSummary(p);
                    const list = (p.recharges||[]).slice(0, MAX_RECHARGE_TARGETS);
                    const slots = list.concat(Array.from({length:MAX_RECHARGE_TARGETS-list.length},()=>({entity:"",pct:0})));
                    const setRec = (k, patch) => {
                      const next = slots.slice();
                      next[k] = { ...next[k], ...patch };
                      setP(p.id, { recharges: next.filter((x)=>x && x.entity && x.pct) });
                    };
                    return (
                      <tr key={"rc"+p.id}>
                        <td colSpan={16} style={{ padding:"12px 16px", background:"#F5F9FF",
                             borderBottom:`0.5px solid ${LINE}` }}>
                          <div style={{ fontSize:11.5, fontWeight:600, color:NAVY, marginBottom:8 }}>
                            Recharges for {p.name} — up to {MAX_RECHARGE_TARGETS} companies
                            <span style={{ fontWeight:400, color: sum.valid?MUT:NEG, marginLeft:8 }}>
                              {sum.pct}% recharged, {sum.retained}% retained{sum.warning ? " · "+sum.warning : ""}
                            </span>
                          </div>
                          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:8 }}>
                            {slots.map((rec,k)=>(
                              <div key={k} style={{ display:"flex", gap:6, alignItems:"center" }}>
                                <span style={{ fontSize:10, color:MUT, width:14 }}>{k+1}</span>
                                <select value={rec.entity||""} onChange={(e)=>setRec(k,{ entity:e.target.value })}
                                  style={{ ...selS, flex:1 }}>
                                  <option value="">— not recharged —</option>
                                  {ENTITIES.filter((x)=>x.ref!==(p.entity||"AFG-IOM")).map((x)=>(
                                    <option key={x.ref} value={x.ref}>{x.name}{x.ref===GROUP_REF?" (on-charged to subs)":""}</option>
                                  ))}
                                </select>
                                <input type="number" min="0" max="100" value={rec.pct||""} placeholder="%"
                                  onChange={(e)=>setRec(k,{ pct:Number(e.target.value)||0 })}
                                  style={{ ...selS, width:60, textAlign:"right" }} />
                                <span style={{ fontSize:10.5, color:MUT }}>%</span>
                                {rec.entity===GROUP_REF && (
                                  <label style={{ display:"flex", alignItems:"center", gap:4, fontSize:10, color:MUT, cursor:"pointer", whiteSpace:"nowrap" }}
                                    title="Tick only if the group is a conduit and should pass this on to the operating companies. Leave clear for a contribution to group costs, which stays at group.">
                                    <input type="checkbox" checked={!!rec.onward}
                                      onChange={()=>setRec(k,{ onward:!rec.onward })}
                                      style={{ width:12, height:12, cursor:"pointer" }} />
                                    pass on
                                  </label>
                                )}
                              </div>
                            ))}
                          </div>
                          <div style={{ fontSize:10.5, color:MUT, marginTop:9, lineHeight:1.6 }}>
                            A charge to <strong>Affinity Group Limited</strong> can mean two different things, so it is
                            set per line. Left clear, it is a <strong>contribution to group</strong> and stays there —
                            the usual case, such as an Isle of Man employee charging 20% across for group work. Ticked
                            <strong> pass on</strong>, the group is a <strong>conduit</strong>: it receives the charge and
                            spreads it to the operating companies on the allocation basis, which is the case for someone
                            paid by one company but working across the whole group.
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ fontSize:12.5, fontWeight:600, color:NAVY, marginBottom:8 }}>Group allocation basis — how the group passes on what it receives</div>
            <div style={{ ...panelBox, marginBottom:18 }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>{["Company","Share of group on-charge"].map((h)=>(<th key={h} style={thS}>{h}</th>))}</tr></thead>
                <tbody>
                  {Object.keys(GROUP_ALLOCATION).map((ref)=>{
                    const e = ENTITIES.find((x)=>x.ref===ref);
                    return (
                      <tr key={ref} style={{ borderBottom:`0.5px solid ${LINE}`,
                          background: ref===entity ? "rgba(0,196,204,0.06)" : "transparent" }}>
                        <td style={tdS}>{e ? e.name : ref}</td>
                        <td style={numS}>{GROUP_ALLOCATION[ref]}%</td>
                      </tr>
                    );
                  })}
                  <tr><td style={{ ...tdS, fontWeight:700, background:SUBTLE }}>Total</td>
                    <td style={{ ...numS, fontWeight:700, background:SUBTLE }}>
                      {Object.values(GROUP_ALLOCATION).reduce((a,b)=>a+b,0)}%
                    </td></tr>
                </tbody>
              </table>
            </div>

            <div style={{ fontSize:12.5, fontWeight:600, color:NAVY, marginBottom:8 }}>Employer on-costs by region</div>
            <div style={{ ...panelBox, marginBottom:8 }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>{["Region","Employer social","Threshold","Ceiling","Pension","Pension ceiling","Heads"].map((h)=>(
                  <th key={h} style={thS}>{h}</th>))}</tr></thead>
                <tbody>
                  {Object.keys(ONCOSTS_BY_REGION).map((k)=>{
                    const r = ONCOSTS_BY_REGION[k];
                    const heads = staff.filter((p)=>(p.region||"IOM")===k).length;
                    return (
                      <tr key={k} style={{ borderBottom:`0.5px solid ${LINE}` }}>
                        <td style={{ ...tdS, fontWeight:600 }}>{r.label}</td>
                        <td style={numS}>{(r.socialPct*100).toFixed(2)}%</td>
                        <td style={numS}>{r.socialThreshold ? nf(r.socialThreshold) : "—"}</td>
                        <td style={numS}>{r.socialCap ? nf(r.socialCap) : "uncapped"}</td>
                        <td style={numS}>{(r.pensionPct*100).toFixed(2)}%</td>
                        <td style={numS}>{r.pensionCap ? nf(r.pensionCap) : "uncapped"}</td>
                        <td style={numS}>{heads || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ background:"#FDF4DC", border:"0.5px solid #E5CE9A", borderRadius:8, padding:"9px 12px",
                          fontSize:10.5, color:"#7B4F1D", lineHeight:1.65, marginBottom:18 }}>
              ⚠️ These rates are placeholders in the right shape, not authoritative figures, and they change every tax
              year. Ceilings are applied cumulatively, so a capped contribution correctly stops once the year's earnings
              pass the limit rather than being charged flat each month. Finance to confirm each region before the budget
              is relied on.
            </div>

            <div style={{ fontSize:12.5, fontWeight:600, color:NAVY, marginBottom:8 }}>Staff cost by month, built from the above</div>
            <div style={{ ...panelBox, overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:900 }}>
                <thead><tr><th style={{ ...thS, minWidth:150 }}>Element</th>
                  {MONTHS.map((m)=><th key={m} style={{ ...thS, textAlign:"right" }}>{m}</th>)}
                  <th style={{ ...thS, textAlign:"right" }}>Year</th></tr></thead>
                <tbody>
                  {[["Salaries","salary"],["Employer social","social"],["Pension","pension"],
                    ["Bonus","bonus"],["Healthcare, wellness, cinema","benefits"]].map(([l,k])=>(
                    <tr key={k} style={{ borderBottom:`0.5px solid ${LINE}` }}>
                      <td style={tdS}>{l}</td>
                      {team[k].map((v,i)=><td key={i} style={numS}>{nf(v)}</td>)}
                      <td style={{ ...numS, fontWeight:600 }}>{nf(team[k].reduce((a,b)=>a+b,0))}</td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ ...tdS, fontWeight:700, background:SUBTLE }}>Total</td>
                    {team.total.map((v,i)=><td key={i} style={{ ...numS, background:SUBTLE, fontWeight:700 }}>{nf(v)}</td>)}
                    <td style={{ ...numS, background:SUBTLE, fontWeight:700 }}>{nf(team.total.reduce((a,b)=>a+b,0))}</td>
                  </tr>
                  <tr>
                    <td style={{ ...tdS, color:MUT }}>Headcount</td>
                    {team.heads.map((v,i)=><td key={i} style={{ ...numS, color:MUT }}>{v}</td>)}
                    <td style={{ ...numS, color:MUT }}>—</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ══════════════ BALANCE SHEET & CASH ══════════════ */}
      {view === "balance" && (() => {
        const totals = phaseFees(fees, 2026);
        const team = phaseHeadcount(staff);
        const overheads = MONTHS.map(()=>18000);
        const costs = team.total.map((v,i)=>v+overheads[i]);
        const bs = projectBalanceSheet({
          invoiced: totals.invoiced, earned: totals.earned, costsIncurred: costs,
          collectionDays, paymentDays,
        });
        return (
          <div style={{ padding:"14px 22px 60px" }}>
            <div style={panelNote}>
              The balance sheet and cash flow are calculated, not budgeted separately. Revenue recognised comes from the
              earned basis; cash comes from the billed basis and the collection period. The gap between the two is
              deferred income. Change the assumptions below and the projection moves.
            </div>

            <div style={{ display:"flex", gap:14, alignItems:"center", margin:"12px 0 14px", flexWrap:"wrap" }}>
              <label style={{ fontSize:11.5, color:MUT, display:"flex", alignItems:"center", gap:6 }}>
                Debtor days
                <input type="number" value={collectionDays} onChange={(e)=>setCollectionDays(Number(e.target.value)||0)}
                  style={{ ...selS, width:70, textAlign:"right" }} />
              </label>
              <label style={{ fontSize:11.5, color:MUT, display:"flex", alignItems:"center", gap:6 }}>
                Creditor days
                <input type="number" value={paymentDays} onChange={(e)=>setPaymentDays(Number(e.target.value)||0)}
                  style={{ ...selS, width:70, textAlign:"right" }} />
              </label>
              <span style={{ fontSize:11, fontWeight:600, borderRadius:20, padding:"3px 10px",
                background: bs.balances?"#E7F4EF":"#FCEBEB", color: bs.balances?"#1F6F54":"#A32D2D" }}>
                {bs.balances ? "Balance sheet balances in every month ✓" : "Does not balance — check assumptions"}
              </span>
              <span style={{ fontSize:11, color:MUT, marginLeft:"auto" }}>
                Closing bank £{nf(bs.closing.bank)} · closing debtors £{nf(bs.closing.receivables)}
              </span>
            </div>

            <div style={{ ...panelBox, overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:1000 }}>
                <thead><tr>
                  {["Month","Invoiced","Earned","Costs","Receipts","Payments","Debtors","Deferred income","Creditors","Bank","Retained"].map((h)=>(
                    <th key={h} style={{ ...thS, textAlign: h==="Month"?"left":"right" }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {bs.rows.map((r)=>(
                    <tr key={r.month} style={{ borderBottom:`0.5px solid ${LINE}` }}>
                      <td style={{ ...tdS, fontWeight:600 }}>{r.month}</td>
                      <td style={numS}>{nf(r.invoiced)}</td>
                      <td style={numS}>{nf(r.earned)}</td>
                      <td style={numS}>{nf(r.costs)}</td>
                      <td style={numS}>{nf(r.receipts)}</td>
                      <td style={numS}>{nf(r.payments)}</td>
                      <td style={numS}>{nf(r.receivables)}</td>
                      <td style={{ ...numS, color: r.deferredIncome>0?AMBER:MUT }}>{nf(r.deferredIncome)}</td>
                      <td style={numS}>{nf(r.payables)}</td>
                      <td style={{ ...numS, fontWeight:600, color: r.bank<0?NEG:INK }}>{nf(r.bank)}</td>
                      <td style={{ ...numS, fontWeight:600, color: r.retained<0?NEG:POS }}>{nf(r.retained)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ fontSize:11, color:MUT, marginTop:10, lineHeight:1.7 }}>
              Assets less liabilities equals retained earnings in every month, which is the check that the projection is
              internally consistent. Per-account assumptions — collection profiles by client, payment terms by supplier —
              are the next layer and need the ledger connected.
            </div>
          </div>
        );
      })()}

      {/* ══════════════ WORKFLOW ══════════════ */}
      {view === "workflow" && (
        <div style={{ padding:"16px 22px 60px" }}>
          {/* Neil's sequence: principles, then owners gather, then two levels of
              review before Group finance sees it. */}
          <div style={{ ...panelNote, marginBottom:14 }}>
            A budget is not just submitted and approved. The year's principles are issued first, each cost centre owner
            gathers their team's requirements, the owner and their MD review and amend, the MD then reviews every centre
            for their office, and the key points are agreed with Group before anything reaches Group finance.
          </div>

          <div style={{ display:"flex", gap:0, marginBottom:18, flexWrap:"wrap" }}>
            {BUDGET_STAGES.map((st, i) => {
              const idx = BUDGET_STAGES.findIndex((x) => x.code === stage);
              const done = i < idx, current = i === idx;
              return (
                <div key={st.code} onClick={() => setStage(st.code)}
                  title={st.note || st.label}
                  style={{ flex:"1 1 150px", minWidth:150, cursor:"pointer", padding:"10px 12px",
                           background: current ? CY : done ? "#E7F4EF" : CARD,
                           color: current ? "#fff" : done ? "#1F6F54" : MUT,
                           borderTop:`3px solid ${current ? NAVY : done ? POS : LINE}`,
                           borderRight:`0.5px solid ${LINE}`, borderBottom:`0.5px solid ${LINE}` }}>
                  <div style={{ fontSize:9.5, opacity:0.8, marginBottom:3 }}>STEP {i + 1}{done ? " · done" : current ? " · here" : ""}</div>
                  <div style={{ fontSize:11.5, fontWeight:600, lineHeight:1.35 }}>{st.label}</div>
                  <div style={{ fontSize:9.5, opacity:0.85, marginTop:3 }}>{st.owner}</div>
                </div>
              );
            })}
          </div>

          <div style={{ fontSize:12.5, fontWeight:600, color:NAVY, marginBottom:8 }}>
            Cost centres and their business owners
          </div>
          <div style={{ ...panelBox, marginBottom:18 }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>{["Cost centre","Business owner","Stage","Wish list","Reviewed with MD"].map((h)=>(
                <th key={h} style={thS}>{h}</th>))}</tr></thead>
              <tbody>
                {COST_CENTRES.map((c, i) => {
                  const st = ["submitted","gathering","owner_md","md_review","gathering","owner_md",
                              "md_review","gathering","submitted","submitted","owner_md"][i] || "gathering";
                  const label = (BUDGET_STAGES.find((x)=>x.code===st) || {}).label;
                  return (
                    <tr key={c.code} style={{ borderBottom:`0.5px solid ${LINE}` }}>
                      <td style={{ ...tdS, fontWeight:600 }}>{c.name}
                        <span style={{ fontSize:9.5, color:MUT, marginLeft:6, fontFamily:"ui-monospace,monospace" }}>{c.code}</span></td>
                      <td style={tdS}>{c.owner}</td>
                      <td style={tdS}>
                        <span style={{ fontSize:10, fontWeight:700, borderRadius:20, padding:"2px 8px",
                          background: st==="submitted"?"#E7F4EF":st==="md_review"?"#FDF4DC":"#EAF0FB",
                          color: st==="submitted"?"#1F6F54":st==="md_review"?"#7B4F1D":"#274690" }}>{label}</span>
                      </td>
                      <td style={{ ...tdS, textAlign:"center" }}>{st==="gathering" ? "in progress" : "✓"}</td>
                      <td style={{ ...tdS, textAlign:"center" }}>{["submitted","md_review","group_disc","approved","locked"].includes(st) ? "✓" : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ background:CARD, border:`0.5px solid ${LINE}`, borderRadius:9, overflow:"hidden", marginBottom:18 }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>
                {["Entity","Owner","Approver","Due","Status","Validation",""].map((h)=>(
                  <th key={h} style={{ padding:"8px 14px", textAlign:"left", fontSize:10, fontWeight:600, color:MUT,
                                       textTransform:"uppercase", letterSpacing:"0.4px", background:SUBTLE, borderBottom:`0.5px solid ${LINE}` }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {ENTITIES.map((e,i)=>{
                  const st = e.ref===entity ? state : ["Approved","Not started","In progress","Not started","Approved","Not started","Submitted"][i];
                  const errs = e.ref===entity ? errors.length : (i===2 ? 2 : 0);
                  return (
                    <tr key={e.ref} style={{ borderBottom:`0.5px solid ${LINE}`, background:e.ref===entity?"rgba(0,196,204,0.05)":"transparent" }}>
                      <td style={{ padding:"9px 14px", fontSize:12.5, fontWeight:e.ref===entity?600:400 }}>{e.name}</td>
                      <td style={{ padding:"9px 14px", fontSize:12 }}>{["Neil Kelly","Neil Kelly","Joanne Fenech","Garry Crossan","Neil Kelly","Andrew Morgan","Andrew Morgan"][i]}</td>
                      <td style={{ padding:"9px 14px", fontSize:12, color:MUT }}>Andrew Morgan</td>
                      <td style={{ padding:"9px 14px", fontSize:12, color:MUT }}>30/09/2026</td>
                      <td style={{ padding:"9px 14px" }}><Badge label={st} /></td>
                      <td style={{ padding:"9px 14px", fontSize:11.5, color: errs?NEG:POS, fontWeight:600 }}>
                        {errs ? errs+" error"+(errs>1?"s":"") : "clear"}
                      </td>
                      <td style={{ padding:"9px 14px" }}>
                        <button style={{ ...btn, padding:"3px 10px", fontSize:10.5 }}
                          onClick={()=>{ setEntity(e.ref); setView("input"); }}>Open ↗</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ fontSize:12.5, fontWeight:600, color:NAVY, marginBottom:8 }}>Approval history — {ent.name}</div>
          <div style={{ background:CARD, border:`0.5px solid ${LINE}`, borderRadius:9, padding:"12px 15px" }}>
            {history.map((h,i)=>(
              <div key={i} style={{ display:"flex", gap:10, padding:"7px 0", borderBottom: i<history.length-1?`0.5px solid ${LINE}`:"none", fontSize:11.5 }}>
                <span style={{ color:MUT, minWidth:130 }}>{h.at}</span>
                <span style={{ fontWeight:600, minWidth:120 }}>{h.who}</span>
                <span style={{ color:MUT }}>{h.what}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══════════════ SCENARIOS ══════════════ */}
      {view === "scenarios" && (
        <div style={{ padding:"16px 22px 60px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:9, marginBottom:11, flexWrap:"wrap" }}>
            <div style={{ fontSize:13, fontWeight:600, color:NAVY }}>Scenarios</div>
            <span style={{ fontSize:11, color:MUT }}>A scenario is a copy of an approved budget. Changing one never touches the approved figures.</span>
            <button style={{ ...btnP, marginLeft:"auto" }}
                    title="A scenario you cannot compare against the approved budget is just a second set of numbers"
                    onClick={async()=>{
                      const a=window.prompt("Approved budget id?"); if(!a) return;
                      const b=window.prompt("Scenario id to compare against it?"); if(!b) return;
                      const r=await compareScenarios(Number(a), Number(b));
                      if(!r||!r.live){ window.alert("Not signed in."); return; }
                      const rows=r.data||[];
                      window.alert(rows.length
                        ? "Where they differ:\n"+rows.slice(0,20).map(x=>
                            `  ${x.account_code} ${x.account_name||""}: ${x.amount_a} vs ${x.amount_b} (${x.variance})`).join("\n")
                        : "They do not differ on any account.");
                    }}>
              Compare to a scenario
            </button>
            <button style={btnP}
                    title="The budget as a whole, rather than account by account"
                    onClick={async()=>{
                      const id=window.prompt("Budget id?"); if(!id) return;
                      const r=await budgetSummary(Number(id));
                      if(!r||!r.live){ window.alert("Not signed in."); return; }
                      const rows=r.data||[];
                      window.alert(rows.length
                        ? rows.slice(0,25).map(x=>`  ${x.account_code} ${x.account_name||""}: ${x.total}`).join("\n")
                        : "Nothing recorded for that budget.");
                    }}>
              Budget summary
            </button>
            <button style={{ ...btnP }} onClick={createScenario}>＋ New scenario from approved budget</button>
          </div>

          <div style={{ background:CARD, border:`0.5px solid ${LINE}`, borderRadius:9, overflow:"hidden", marginBottom:18 }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>
                {["Scenario","Based on","Owner","Created","Status","Net result vs budget",""].map((h)=>(
                  <th key={h} style={{ padding:"8px 14px", textAlign:"left", fontSize:10, fontWeight:600, color:MUT,
                                       textTransform:"uppercase", letterSpacing:"0.4px", background:SUBTLE, borderBottom:`0.5px solid ${LINE}` }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {SCENARIOS_SEED.map((sc)=>(
                  <tr key={sc.id} style={{ borderBottom:`0.5px solid ${LINE}`, background:sc.name===scenario?"rgba(0,196,204,0.05)":"transparent" }}>
                    <td style={{ padding:"9px 14px", fontSize:12.5, fontWeight:600 }}>
                      {sc.name} {sc.locked && <span title="Locked" style={{ marginLeft:5, fontSize:9, color:"#4A4A6A", background:"#EEEEF5", borderRadius:8, padding:"1px 6px" }}>LOCKED</span>}
                    </td>
                    <td style={{ padding:"9px 14px", fontSize:12, color:MUT }}>{sc.base || "—"}</td>
                    <td style={{ padding:"9px 14px", fontSize:12 }}>{sc.owner}</td>
                    <td style={{ padding:"9px 14px", fontSize:12, color:MUT }}>{sc.created.split("-").reverse().join("/")}</td>
                    <td style={{ padding:"9px 14px" }}><Badge label={sc.status==="Draft"?"Not started":sc.status} /></td>
                    <td style={{ padding:"9px 14px", fontSize:12.5, fontWeight:700, fontVariantNumeric:"tabular-nums",
                                 color: sc.delta>0?POS : sc.delta<0?NEG : MUT }}>
                      {sc.delta===0 ? "baseline" : (sc.delta>0?"+":"") + sc.delta.toFixed(1) + "%"}
                    </td>
                    <td style={{ padding:"9px 14px", whiteSpace:"nowrap" }}>
                      <button style={{ ...btn, padding:"3px 10px", fontSize:10.5 }}
                        onClick={()=>{ setScenario(sc.name); setView("input"); }}>Open ↗</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ fontSize:12.5, fontWeight:600, color:NAVY, marginBottom:8 }}>Comparison — effect of the change, not two spreadsheets side by side</div>
          <div style={{ background:CARD, border:`0.5px solid ${LINE}`, borderRadius:9, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>
                {["Driver","FY26 Budget","FY26 Downside","Change","Effect on net result"].map((h)=>(
                  <th key={h} style={{ padding:"8px 14px", textAlign:"left", fontSize:10, fontWeight:600, color:MUT,
                                       textTransform:"uppercase", letterSpacing:"0.4px", background:SUBTLE, borderBottom:`0.5px solid ${LINE}` }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {[
                  ["New entity take-on (per month)", "6", "3", "−50%", -184000],
                  ["Average annual fee",             "£4,150", "£3,900", "−6.0%", -71000],
                  ["Attrition rate",                 "4.0%", "6.5%", "+2.5pp", -96000],
                  ["Salary inflation",               "3.5%", "3.5%", "no change", 0],
                  ["Headcount at year end",          "58", "55", "−3", 112000],
                ].map((r,i)=>(
                  <tr key={i} style={{ borderBottom:`0.5px solid ${LINE}` }}>
                    <td style={{ padding:"9px 14px", fontSize:12.5 }}>{r[0]}</td>
                    <td style={{ padding:"9px 14px", fontSize:12.5, fontVariantNumeric:"tabular-nums" }}>{r[1]}</td>
                    <td style={{ padding:"9px 14px", fontSize:12.5, fontVariantNumeric:"tabular-nums" }}>{r[2]}</td>
                    <td style={{ padding:"9px 14px", fontSize:12, color:MUT }}>{r[3]}</td>
                    <td style={{ padding:"9px 14px", fontSize:12.5, fontWeight:700, fontVariantNumeric:"tabular-nums",
                                 color: r[4]>0?POS : r[4]<0?NEG : MUT }}>
                      {r[4]===0 ? "—" : (r[4]>0?"+":"−") + "£" + Math.abs(r[4]).toLocaleString("en-GB")}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td style={{ padding:"10px 14px", fontSize:12.5, fontWeight:700, background:SUBTLE }}>Total effect</td>
                  <td colSpan={3} style={{ background:SUBTLE }} />
                  <td style={{ padding:"10px 14px", fontSize:13, fontWeight:700, background:SUBTLE, color:NEG, fontVariantNumeric:"tabular-nums" }}>−£239,000</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══════════════ VARIANCE & ANALYSIS ══════════════ */}
      {view === "variance" && (
        <div style={{ padding:"16px 22px 60px" }}>

          {/* Key metrics, carried over from Budgets */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))", gap:10, marginBottom:18 }}>
            {[
              ["Budget — full year", "£2,100,000", NAVY],
              ["Forecast — full year", "£2,203,000", CY],
              ["Actual YTD", "£1,081,000", NAVY],
              ["Variance YTD", "+£22,000", POS],
              ["Net margin", "28.4%", NAVY],
            ].map(([l,v,c])=>(
              <div key={l} style={{ background:CARD, border:`0.5px solid ${LINE}`, borderRadius:9, padding:"12px 14px" }}>
                <div style={{ fontSize:10.5, color:MUT, marginBottom:5 }}>{l}</div>
                <div style={{ fontSize:19, fontWeight:700, color:c, fontVariantNumeric:"tabular-nums" }}>{v}</div>
              </div>
            ))}
          </div>

          <div style={{ fontSize:12.5, fontWeight:600, color:NAVY, marginBottom:8 }}>Variance analysis — YTD</div>
          <div style={{ background:CARD, border:`0.5px solid ${LINE}`, borderRadius:9, overflow:"hidden", marginBottom:20 }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>
                {["Line","Budget","Actual YTD","Variance","%","Status",""].map((h)=>(
                  <th key={h} style={{ padding:"8px 14px", textAlign:"left", fontSize:10, fontWeight:600, color:MUT,
                                       textTransform:"uppercase", letterSpacing:"0.4px", background:SUBTLE, borderBottom:`0.5px solid ${LINE}` }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {VARIANCE.map((v,i)=>(
                  <tr key={i} style={{ borderBottom:`0.5px solid ${LINE}` }}>
                    <td style={{ padding:"9px 14px", fontSize:12.5 }}>{v.line}</td>
                    <td style={{ padding:"9px 14px", fontSize:12.5, fontVariantNumeric:"tabular-nums" }}>£{nf(v.budget)}</td>
                    <td style={{ padding:"9px 14px", fontSize:12.5, fontVariantNumeric:"tabular-nums" }}>£{nf(v.actual)}</td>
                    <td style={{ padding:"9px 14px", fontSize:12.5, fontWeight:700, fontVariantNumeric:"tabular-nums",
                                 color: v.variance>0?POS:NEG }}>{v.variance>0?"+":"−"}£{nf(Math.abs(v.variance))}</td>
                    <td style={{ padding:"9px 14px", fontSize:12, color:MUT }}>{v.pct}</td>
                    <td style={{ padding:"9px 14px" }}>
                      <span style={{ fontSize:10, fontWeight:700, borderRadius:20, padding:"2px 9px",
                                     background: v.status==="Favourable"?"#E7F4EF":"#FCEBEB",
                                     color: v.status==="Favourable"?"#1F6F54":"#A32D2D" }}>{v.status}</span>
                    </td>
                    <td style={{ padding:"9px 14px" }}>
                      <button style={{ ...btn, padding:"3px 9px", fontSize:10 }}
                        onClick={()=>{ setView("input"); setDrawer("comments"); }}
                        title="Attach an explanation to this variance">＋ Explain</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ fontSize:12.5, fontWeight:600, color:NAVY, marginBottom:8 }}>Revenue by service line — budget vs forecast vs actual</div>
          <div style={{ background:CARD, border:`0.5px solid ${LINE}`, borderRadius:9, overflow:"hidden", marginBottom:20 }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>
                {["Service line","Budget","Forecast","Actual YTD","Margin %","Forecast vs budget"].map((h)=>(
                  <th key={h} style={{ padding:"8px 14px", textAlign:"left", fontSize:10, fontWeight:600, color:MUT,
                                       textTransform:"uppercase", letterSpacing:"0.4px", background:SUBTLE, borderBottom:`0.5px solid ${LINE}` }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {SERVICELINES.map((r,i)=>{
                  const d = r.forecast - r.budget;
                  return (
                    <tr key={i} style={{ borderBottom:`0.5px solid ${LINE}` }}>
                      <td style={{ padding:"9px 14px", fontSize:12.5 }}>{r.line}</td>
                      <td style={{ padding:"9px 14px", fontSize:12.5, fontVariantNumeric:"tabular-nums" }}>£{nf(r.budget)}</td>
                      <td style={{ padding:"9px 14px", fontSize:12.5, fontVariantNumeric:"tabular-nums" }}>£{nf(r.forecast)}</td>
                      <td style={{ padding:"9px 14px", fontSize:12.5, fontVariantNumeric:"tabular-nums", color:MUT }}>£{nf(r.actual)}</td>
                      <td style={{ padding:"9px 14px", fontSize:12.5, fontWeight:600 }}>{r.margin}%</td>
                      <td style={{ padding:"9px 14px", fontSize:12.5, fontWeight:700, fontVariantNumeric:"tabular-nums", color: d>=0?POS:NEG }}>
                        {d>=0?"+":"−"}£{nf(Math.abs(d))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ fontSize:12.5, fontWeight:600, color:NAVY, marginBottom:8 }}>Monthly revenue — budget vs forecast vs actual</div>
          <div style={{ background:CARD, border:`0.5px solid ${LINE}`, borderRadius:9, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>
                {["Month","Budget","Forecast","Actual","Actual vs budget"].map((h)=>(
                  <th key={h} style={{ padding:"8px 14px", textAlign:"left", fontSize:10, fontWeight:600, color:MUT,
                                       textTransform:"uppercase", letterSpacing:"0.4px", background:SUBTLE, borderBottom:`0.5px solid ${LINE}` }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {MONTHLY.map((m,i)=>{
                  const d = m.actual == null ? null : m.actual - m.budget;
                  return (
                    <tr key={i} style={{ borderBottom:`0.5px solid ${LINE}` }}>
                      <td style={{ padding:"9px 14px", fontSize:12.5 }}>{m.month}</td>
                      <td style={{ padding:"9px 14px", fontSize:12.5, fontVariantNumeric:"tabular-nums" }}>£{nf(m.budget)}</td>
                      <td style={{ padding:"9px 14px", fontSize:12.5, fontVariantNumeric:"tabular-nums" }}>£{nf(m.forecast)}</td>
                      <td style={{ padding:"9px 14px", fontSize:12.5, fontVariantNumeric:"tabular-nums", color: m.actual==null?"#bbb":INK }}>
                        {m.actual==null ? "not yet posted" : "£"+nf(m.actual)}
                      </td>
                      <td style={{ padding:"9px 14px", fontSize:12.5, fontWeight:700, fontVariantNumeric:"tabular-nums",
                                   color: d==null?"#bbb":d>=0?POS:NEG }}>
                        {d==null ? "—" : (d>=0?"+":"−")+"£"+nf(Math.abs(d))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ margin:"0 22px 30px", background:"#FDF4DC", border:"0.5px solid #E5CE9A", borderRadius:8,
                    padding:"10px 13px", fontSize:10.5, color:"#7B4F1D", lineHeight:1.65 }}>
        ⚠️ Interface layer. The grid, workflow states, validation, scenarios and comparison are complete and behave as they will in production, against preview figures. The calculation engine they map to already exists on the <code>accounting-engine</code> branch — submit_budget, approve_budget, build_rolling_forecast, compare_budget_scenarios — and connecting them needs that branch merged and db/001–051 run. Accounting policy for account mappings and any consolidation treatment needs finance sign-off before this is used for real budgets.
      </div>
    </div>
  );
}
